import { t } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonVariant } from 'src/components/atoms/Button';
import { BankLinkingTile } from 'src/components/DUP/molecules/BankLinkingTile/BankLinkingTile';
import { CollapsibleToggle } from 'src/components/DUP/molecules/CollapsibleToggle/CollapsibleToggle';
import ConnectedPayrollTile from 'src/components/DUP/molecules/ConnectedPayrollTile/ConnectedPayrollTile';
import {
  ManualUploadProps,
  ManualUploadTile
} from 'src/components/DUP/molecules/ManualUploadTile/ManualUploadTile';
import { HowToLink } from 'src/components/DUP/molecules/ConnectedPayrollTile/styles';
import { HelpSection } from 'src/components/DUP/molecules/WizardSupportingDocuments/WizardSupportingDocuments';
import {
  ButtonsContainer,
  Container
} from 'src/components/DUP/molecules/WizardProofOfIncome/styles';
import { useApplication } from 'src/context/ApplicationContext';
import { DOCUMENT_INSTRUCTIONS_URL } from 'src/features/DUP';
import { LanguageOptions } from 'src/i18n/i18n';
import { StyledButton } from 'src/pages/DUPPage/styles';
import {
  getRequiredDocAmt,
  REQUIRED_DOCS_TRANSLATION_PREFIX
} from 'src/pages/DUPPage/useRequiredDocsModal';
import { UnauthenticateSessionProofType } from 'src/types/api';

const MANUAL_PAYROLL_TRANSLATION_PREFIX = 'dup.wizard.manualPayrollTile';
const BANK_LINKING_TRANSLATION_PREFIX = 'dup.wizard.bankLinkingTile';

export const WizardProofOfIncome = () => {
  const {
    connectedPayrollEnabled,
    property,
    connectedPayroll: { isLoadingAccounts },
    bankLinking: { isLoadingBankAccounts },
    isBankLinkingEnabled,
    proofs,
    bankLinking,
    steps: { goBack, onFinished }
  } = useApplication();

  const proofType = UnauthenticateSessionProofType.Paystub;
  const manualUploads = proofs.filter((proof) => proof.type === proofType);
  const bankAccountsConnected = bankLinking.bankAccountsConnected;
  const manualTileProps: ManualUploadProps = {
    title: t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.title`),
    subtitle: connectedPayrollEnabled
      ? t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.subtitle.connectPayroll`)
      : undefined,
    buttonVariant: connectedPayrollEnabled ? ButtonVariant.outline : undefined,
    textContent: [
      <Trans key="manual-payroll-content-0">
        {t(`${REQUIRED_DOCS_TRANSLATION_PREFIX}paystubRequirement`, {
          count: getRequiredDocAmt(proofType, property),
          propertyName: property.name
        })}
      </Trans>,
      connectedPayrollEnabled
        ? t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.text.connectPayroll`)
        : undefined,
      <Trans key="manual-payroll-content-1">
        {t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.text.1`)}
      </Trans>,
      <>
        {t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.text.2.part1`)}
        <DocumentInstructionsLink>
          {t(`${MANUAL_PAYROLL_TRANSLATION_PREFIX}.text.2.part2`)}
        </DocumentInstructionsLink>
      </>
    ].filter(Boolean),
    proofType
  };
  const bankLinkingProps = {
    title: t(`${BANK_LINKING_TRANSLATION_PREFIX}.title`),
    subtitle: t(`${BANK_LINKING_TRANSLATION_PREFIX}.subtitle`),
    buttonVariant: ButtonVariant.outline,
    textContent: [
      <>{t(`${BANK_LINKING_TRANSLATION_PREFIX}.text.1`)}</>,
      <>{t(`${BANK_LINKING_TRANSLATION_PREFIX}.text.2`)}</>,
      <>
        {t(`${BANK_LINKING_TRANSLATION_PREFIX}.text.3.part1`)},
        <DocumentInstructionsLink>
          {t(`${BANK_LINKING_TRANSLATION_PREFIX}.text.3.part2`)}
        </DocumentInstructionsLink>
      </>
    ]
  };

  const showLoadingIndicator = isLoadingBankAccounts || isLoadingAccounts;

  return (
    <Container>
      {connectedPayrollEnabled ? (
        <>
          <ConnectedPayrollTile />
          <CollapsibleToggle
            defaultExpanded={!!manualUploads.length || !!bankAccountsConnected?.length}
            toggleText={{
              expanded: t('dup.wizard.collapsibleSection.hide'),
              collapsed: t('dup.wizard.collapsibleSection.show')
            }}
          >
            <ManualUploadTile {...manualTileProps} />
            {isBankLinkingEnabled ? <BankLinkingTile {...bankLinkingProps} /> : <></>}
          </CollapsibleToggle>
        </>
      ) : (
        <ManualUploadTile {...manualTileProps} />
      )}
      <HelpSection />
      <ButtonsContainer justify="space-between">
        <StyledButton
          name="back"
          onClick={() => {
            goBack();
          }}
          variant={ButtonVariant.outline}
        >
          {t('buttonLabel.back')}
        </StyledButton>
        <StyledButton
          name="next"
          onClick={onFinished}
          variant={ButtonVariant.contained}
          isLoading={showLoadingIndicator}
          isDisabled={showLoadingIndicator}
        >
          {t('buttonLabel.next')}
        </StyledButton>
      </ButtonsContainer>
    </Container>
  );
};

export default WizardProofOfIncome;

export const DocumentInstructionsLink = ({ children }: { children: React.ReactNode }) => {
  const {
    i18n: { resolvedLanguage }
  } = useTranslation();

  return (
    <HowToLink
      href={DOCUMENT_INSTRUCTIONS_URL[(resolvedLanguage as LanguageOptions) ?? 'en']}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </HowToLink>
  );
};
